/**
 * reusable register flow component
 *
 * On successful submission, an email will be sent to the user with a link to set their password and complete registration via existing onboarding util methods
 *
 */
// import primary libraries
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation, Link, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// actions
import { sendSubmitSelfSignup } from '../../groupUser/groupUserStore.js';
import { useLoggedInUser } from '../authService.js';
import { useGetGroup } from '../../group/groupService.js';
import { publicGroupLandingPageEndpoint } from '../../group/groupStore.js';

// utils
import apiUtils from '../../../global/utils/api.js';
import { isSafeUrl, safeRedirect } from '../../../global/utils/linkUtils.js';

// import user components
import { EmailInput, PasswordInput, validateEmail } from '../../../global/components/forms/index.js';
import Spinner from '../../../global/components/helpers/Spinner.jsx';
import StyledButton from 'shared-resources/components/src/StyledButton';
import CheckboxInput from '../../../global/components/forms/CheckboxInput.jsx';

const UserSignupFlow = ({
  wrapperClass
  , logoUrl = 'https://pro-ficiency-static.s3.us-east-2.amazonaws.com/atlas/slp_full_blue.png' // default logo - params only work when value is undefined, but not when it's null
  , welcomeMessage = 'Register a new account to view all content and resources via Simulations Plus.'
  , handleClose
}) => {

  const dispatch = useDispatch();
  const location = useLocation();

  const [userEmail, setUserEmail] = useState('');
  const [inputEmail, setInputEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [error, setError] = useState('');
  const [isTermsAgreementChecked, setIsTermsAgreementChecked] = useState(false);

  const { groupId } = useParams();
  const loggedInUser = useLoggedInUser();

  const finalLogoUrl = logoUrl || 'https://pro-ficiency-static.s3.us-east-2.amazonaws.com/atlas/slp_full_blue.png';

  // not sold on how this whole component is structured - just tried to make it close to the login flow
  // this might be temporary depending on how we want to handle a loggedInUser joining a group
  // we can display a link directly to the group after joining
  // or tell them to go to their dashboard and say it's under the group's organization
  const groupLandingPageEndpoint = publicGroupLandingPageEndpoint(groupId);
  const { data: group, ...groupQuery } = useGetGroup(groupLandingPageEndpoint);

  useEffect(() => {
    // this will run on mount and show a different view if the user is already logged in
    // what happens after join group? return to landing page where view dashboard button is shown?
    if (loggedInUser) {
      setInputEmail(loggedInUser.username);
      setIsEmailValid(validateEmail(loggedInUser.username));
    }
  }, [loggedInUser]);

  const handleSubmitEmail = async (e) => {
    setError('');
    e.preventDefault();
    const { error } = await dispatch(sendSubmitSelfSignup({ groupId, inputEmail, isTermsAgreementChecked })); // send submit email
    if (error) {
      setError(error?.message || "There was a problem registering your account. Please try again");
      // reset password field on error
      setUserEmail(inputEmail);
    } else {
      setUserEmail(inputEmail);
    }
  }

  const resetForm = () => {
    setError('');
    setUserEmail('');
    setInputEmail('');
  }

  return (
    <div className={wrapperClass}>
      <div className="flex flex-col items-center justify-start max-w-full md:w-[550px] md:max-w-[550px] gap-2 px-4">
        {!userEmail ? (
          <div className='flex flex-col gap-5 p-4 rounded-lg bg-neutral-pri w-full max-w-full'>
            <div className='flex justify-center md:p-4'>
              <img className="w-auto h-auto max-h-[30px]" src={finalLogoUrl} alt='logo' />
            </div>
            <div className='flex flex-col gap-2'>
              <h1 className='text-neutral-sec text-3xl m-0'>
                {loggedInUser ? "Join group" : "Sign up"}
              </h1>
              <p className="text-sm">
                {loggedInUser ? "Join the group to access exclusive content and resources." : welcomeMessage}
              </p>
            </div>

            {/* Signup Form - Different flow for logged-in users joining a group */}
            {loggedInUser ? (
              <div>
                <div className='flex mb-2'>
                  <CheckboxInput
                    label={
                      <div className='flex'>
                        <div className="flex gap-1">
                          <p>I agree to the</p>
                          <a href="https://www.simulations-plus.com/terms-of-use/" target="_blank" rel="noreferrer" className="text-xs no-underline my-auto">Terms of Use</a>
                          <p> and </p>
                          <a href="https://www.simulations-plus.com/privacy-notice/" target="_blank" rel="noreferrer" className="text-xs no-underline my-auto">Privacy Policy</a>
                        </div>
                        <p>.</p>
                      </div>
                    }
                    name='termsAgreement'
                    value={isTermsAgreementChecked}
                    change={() => setIsTermsAgreementChecked(!isTermsAgreementChecked)}
                  />
                </div>
                <StyledButton
                  className='w-full'
                  disabled={!inputEmail || !isEmailValid || !isTermsAgreementChecked}
                  onClick={handleSubmitEmail}
                >
                  Join group &rarr;
                </StyledButton>
              </div>
            ) : (
              <form name='userSignupForm' onSubmit={handleSubmitEmail} className='min-w-[200px] md:w-full'>
                <label htmlFor='username' className='mb-2 text-sm'>Enter your email to get started</label>
                <EmailInput
                  name='username'
                  value={inputEmail}
                  disabled={loggedInUser ? true : false}
                  change={e => setInputEmail(e.target.value)}
                  autoFocus={true}
                  placeholder="name@company.com"
                  onValidationChange={setIsEmailValid}
                />
                <div className='my-4 flex gap-2'>
                  <CheckboxInput
                    label={
                      <div className='flex'>
                        <div className="flex gap-1">
                          <p>I agree to the</p>
                          <a href="https://www.simulations-plus.com/terms-of-use/" target="_blank" rel="noreferrer" className="text-xs no-underline my-auto">Terms of Use</a>
                          <p> and </p>
                          <a href="https://www.simulations-plus.com/privacy-notice/" target="_blank" rel="noreferrer" className="text-xs no-underline my-auto">Privacy Policy</a>
                        </div>
                        <p>.</p>
                      </div>
                    }
                    name='termsAgreement'
                    value={isTermsAgreementChecked}
                    change={() => setIsTermsAgreementChecked(!isTermsAgreementChecked)}
                  />
                </div>
                <StyledButton
                  className='w-full'
                  disabled={!inputEmail || !isEmailValid || !isTermsAgreementChecked}
                >
                  Continue &rarr;
                </StyledButton>
              </form>
            )}
          </div>
        ) : error ? (
          // Error state - allows user to try again
          <div className='flex flex-col gap-5 p-4 rounded-lg bg-neutral-pri w-full max-w-full' role='alert'>
            <div className='flex justify-center md:p-4'>
              <img className="w-auto h-auto max-h-[30px]" src={finalLogoUrl} alt='logo' />
            </div>
            <div className='flex flex-col gap-2 bg-error border border-neutral-pri text-error px-4 py-3 rounded relative'>
              <h1 className='text-neutral-sec text-5xl m-0'> Oops! </h1>
              <p>{error}</p>
            </div>
            <StyledButton
              className='mt-4'
              onClick={() => resetForm()}
            >
              Try Again
            </StyledButton>
          </div>
        ) : loggedInUser ? (
          // Success state for logged-in user joining a group
          <div className='flex flex-col gap-5 p-4 rounded-lg bg-neutral-pri w-full max-w-full'>
            <div className='flex justify-center md:p-4'>
              <img className="w-auto h-auto max-h-[30px]" src={finalLogoUrl} alt='logo' />
            </div>
            <div className='flex flex-col gap-8 px-4 py-3 rounded'>
              <h1 className='text-neutral-sec text-5xl m-0'> Success! </h1>
              <div className="flex flex-col gap-2">
                <p>You've successfully joined the group!</p>
                <p>Click below to view the group in your dashboard and access new resources.</p>
              </div>
              <div className='flex flex-col gap-4'>
                <StyledButton
                  to={`/${group?._organization}/${group?._id}`}
                >
                  View group
                </StyledButton>
                <StyledButton
                  variant="secondary"
                  onClick={handleClose}
                >
                  Close
                </StyledButton>
              </div>
            </div>
          </div>
        ) : (
          // Success state for new user signup
          <div className='flex flex-col gap-5 p-4 rounded-lg bg-neutral-pri w-full max-w-full'>
            <div className='flex justify-center md:p-4'>
              <img className="w-auto h-auto max-h-[30px]" src={finalLogoUrl} alt='logo' />
            </div>
            <div className='flex flex-col gap-8 px-4 py-3 rounded'>
              <h1 className='text-neutral-sec text-5xl m-0'> Success! </h1>
              <p>We've sent an email to <span className="font-semibold">{userEmail}.</span></p>
              <p>Click the link in the email and follow the instructions to set up your new account.</p>
              <StyledButton
                className='mt-4'
                onClick={handleClose}
              >
                Close
              </StyledButton>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

UserSignupFlow.propTypes = {
  wrapperClass: PropTypes.string
  , logoUrl: PropTypes.string
  , welcomeMessage: PropTypes.string
  , handleClose: PropTypes.func
}

const LoginOptionsHeader = ({ userEmail, resetForm, resetLink, children, logoUrl }) => {

  return (
    <>
      <div className='flex justify-center p-2 md:p-4'>
        <img className="w-auto h-auto max-h-[30px]" src={logoUrl} alt='logo' />
      </div>
      <h1 className='text-neutral-sec text-5xl'> Welcome! </h1>
      {resetLink ? (
        <Link className='text-2xl text-neutral-pri mt-8 font-semibold hover:cursor-pointer hover:no-underline group' to={isSafeUrl(resetLink)} onClick={resetForm}>
          <span>{userEmail}</span>
          <span className='text-transparent text-sm group-hover:text-error transition-all ml-2'>clear email</span>
        </Link>
      ) : (
        <div className='text-2xl text-neutral-pri mt-8 font-semibold hover:cursor-pointer hover:no-underline group' onClick={resetForm}>
          <span>{userEmail}</span>
          <span className='text-transparent text-sm group-hover:text-error transition-all ml-2'>clear email</span>
        </div>
      )
      }
      {children}
    </>
  )
}

LoginOptionsHeader.propTypes = {
  userEmail: PropTypes.string
  , resetForm: PropTypes.func
  , resetLink: PropTypes.string
  , logoUrl: PropTypes.string
  , children: PropTypes.node
}


const getFriendlyError = (error) => {
  switch (error) {
    // ref returns on SessionController samlLogin and samlLoginCallback
    case 'provider_error_1':
      return 'Failed to Initiate SAML Login with Provider';
    case 'provider_error_2':
      return 'Error Authenticating with SAML Login Provider';
    case 'provider_error_3':
      return 'Failed to Authenticate with SAML Login Provider';
    case 'no_matching_user_1':
      return 'Unable to Locate Matching User. Please try again.';
    case 'no_matching_user_2':
      return 'Unable to Locate Matching User. Please try again.';
    case 'not_allowed_1':
      return 'Permission Denied. Please try again.';
    case 'not_allowed_2':
      return 'Unable To Log In As Global Admin Via SSO. Please Contact Admin.';
    default:
      return error;
  }
}

export default UserSignupFlow;
